import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import './missionControl.css';
import SEOHead from '../components/UI/SEOHead';
import { BlogCoverCard } from '../components/UI/BlogCoverCard';
import { waLink } from '../config';
import { ALL_POSTS, findPostBySlug, type BlogPostMeta } from '../data/blogPosts';
import { BLOG_BODIES } from '../data/blogPostBodies';

/* ──────────────────────────────────────────────────────────────────────────
   Artículo individual — llevado al mismo lenguaje "mission control" oscuro
   del listado (BlogPage.tsx) en vez del viejo estilo "minimalist" claro, para
   que /blog y /blog/:slug se sientan como una sola sección, no dos sitios
   distintos. Reutiliza BlogCoverCard para "relacionados" y el mismo CTA que
   el listado (misma copy, misma tarjeta con glow).
   ────────────────────────────────────────────────────────────────────────── */

// El destacado es el único post cuyo título viene partido en dos claves i18n
// (…_pre + …_grad, pensadas para pintarse con degradado) — el resto tiene una
// clave `_title` única. SEOHead necesita un string plano, así que aquí se
// concatenan (featured_title_pre ya trae el espacio final en los 3 locales).
const getPlainTitle = (post: BlogPostMeta, t: (key: string) => string) =>
  post.key === 'featured' ? t('blog_page.featured_title_pre') + t('blog_page.featured_title_grad') : t(`blog_page.${post.key}_title`);

const BlogPostPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const post = findPostBySlug(slug);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const related = ALL_POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);
  // El cuerpo completo del artículo solo existe redactado en español por ahora
  // (ver comentario en blogPostBodies.ts) — en EN/PT se sigue mostrando el
  // excerpt más un aviso, en vez de mezclar idiomas dentro del mismo artículo.
  const body = i18n.language.startsWith('es') ? BLOG_BODIES[post.key] : undefined;
  const plainTitle = getPlainTitle(post, t);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="mc-page"
    >
      <SEOHead
        title={plainTitle}
        description={t(`blog_page.${post.key}_excerpt`)}
        path={`/blog/${post.slug}`}
        ogImage={`https://ruraydigital.com${post.image}`}
      />

      {/* ── HERO — foto de portada a pantalla completa, fijo en oscuro (igual que el hero del listado) ── */}
      <div className="blog-post-hero" style={{ position: 'relative' }}>
        <div className="container" style={{ paddingTop: 'clamp(6.5rem, 12vh, 8.5rem)', paddingBottom: '2rem' }}>
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            onClick={() => navigate('/blog')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: '1.75rem', cursor: 'pointer', border: 'none', background: 'transparent', color: 'rgba(255,255,255,.6)', fontSize: '.85rem', fontWeight: 600, fontFamily: 'var(--font-body)', padding: 0 }}
          >
            <ArrowLeft size={16} /> {t('blog_post.back')}
          </motion.button>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="blog-cover-card"
            style={{ position: 'relative', minHeight: 'clamp(320px, 42vw, 460px)', borderRadius: 22, overflow: 'hidden' }}
          >
            <div aria-hidden style={{ position: 'absolute', inset: 0, backgroundImage: `url(${post.image})`, backgroundSize: 'cover', backgroundPosition: post.imagePosition ?? 'center' }} />
            {/* Antes era un degradado vertical parejo en todo el ancho, así
                que la foto de fondo quedaba muy tapada de lado a lado. Ahora
                solo la mitad izquierda (donde vive el texto: tag, título,
                meta) se oscurece; la derecha deja ver la foto con claridad. */}
            <div aria-hidden style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(6,6,7,.96) 0%, rgba(6,6,7,.88) 30%, rgba(6,6,7,.55) 48%, transparent 62%)' }} />
            {/* Franja tenue abajo, solo para que la fila de meta (autor ·
                fecha · lectura) no quede pegada directo sobre la foto sin
                ningún velo, incluso del lado izquierdo donde ya hay overlay. */}
            <div aria-hidden style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent 55%, rgba(6,6,7,.5) 100%)' }} />
            <div style={{ position: 'relative', zIndex: 1, padding: 'clamp(2rem, 4.5vw, 3.25rem) clamp(2rem, 4.5vw, 3.25rem) clamp(2.25rem, 4vw, 3rem)' }}>
              <span
                style={{ display: 'inline-flex', fontSize: '.7rem', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', padding: '.35rem .85rem', borderRadius: 999, border: '1px solid rgba(0,229,209,.3)', color: 'var(--accent-teal)', background: 'rgba(0,229,209,.12)', marginBottom: '1.1rem' }}
              >
                {t(`blog_page.${post.key}_tag`)}
              </span>
              <h1 style={{ fontFamily: 'var(--font-heading)', color: '#fff', fontSize: 'clamp(1.9rem, 4.2vw, 3rem)', fontWeight: 700, lineHeight: 1.16, letterSpacing: '-0.02em', margin: '0 0 1.1rem', maxWidth: '38rem' }}>
                {post.key === 'featured' ? (
                  <>
                    {t('blog_page.featured_title_pre')}
                    <span className="grad-text">{t('blog_page.featured_title_grad')}</span>
                  </>
                ) : (
                  plainTitle
                )}
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', flexWrap: 'wrap', color: '#8e8e93', fontSize: '.85rem' }}>
                <span>{t('blog_post.author')}</span>
                <span aria-hidden>·</span>
                <span>{t(`blog_page.${post.key}_date`)}</span>
                <span aria-hidden>·</span>
                <span>{t(`blog_page.${post.key}_read`)} {t('blog_page.read_time_suffix')}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ── CUERPO — theme-aware (respeta el toggle claro/oscuro del sitio) ── */}
      <section style={{ padding: 'clamp(3rem, 6vw, 4.5rem) 0' }}>
        <div className="container" style={{ maxWidth: 760 }}>
          {body ? (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
              {body.map((block, i) =>
                block.type === 'h2' ? (
                  <h2 key={i} style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-primary)', fontSize: 'clamp(1.3rem, 2.4vw, 1.6rem)', fontWeight: 700, letterSpacing: '-0.01em', margin: '2.2rem 0 1rem' }}>
                    {block.text}
                  </h2>
                ) : (
                  <p
                    key={i}
                    style={
                      i === 0
                        ? { color: 'var(--text-primary)', fontSize: '1.15rem', lineHeight: 1.7, fontWeight: 500, margin: '0 0 1.5rem', textAlign: 'justify' }
                        : { color: 'var(--text-secondary)', fontSize: '1rem', lineHeight: 1.75, margin: '0 0 1.25rem', textAlign: 'justify' }
                    }
                  >
                    {block.text}
                  </p>
                )
              )}
            </motion.div>
          ) : (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
              <p style={{ color: 'var(--text-primary)', fontSize: '1.15rem', lineHeight: 1.7, fontWeight: 500, margin: '0 0 1rem', textAlign: 'justify' }}>
                {t(`blog_page.${post.key}_excerpt`)}
              </p>
              <p style={{ color: 'var(--text-secondary)', fontSize: '.9rem', fontStyle: 'italic' }}>
                {t('blog_post.es_only_note', 'El artículo completo todavía solo está redactado en español.')}
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* ── RELACIONADOS — mismas tarjetas que el listado (BlogCoverCard) ── */}
      {related.length > 0 && (
        <section style={{ padding: '0 0 clamp(3rem, 6vw, 4.5rem)' }}>
          <div className="container">
            <span className="mc-mono" style={{ display: 'block', fontSize: '.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.15em', color: 'var(--text-secondary)', opacity: 0.7, marginBottom: '1.25rem' }}>
              {t('blog_post.related')}
            </span>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
              {related.map((p, i) => (
                <BlogCoverCard key={p.key} post={p} variant="grid" onClick={() => navigate(`/blog/${p.slug}`)} delay={i * 0.07} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CTA — idéntico al del listado (misma copy, misma tarjeta) ── */}
      <section style={{ padding: 'clamp(4.5rem, 9vh, 7rem) 0 clamp(5rem, 10vh, 8rem)', position: 'relative' }}>
        <div className="container">
          <motion.div
            className="prod-cta"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            style={{
              position: 'relative', overflow: 'hidden', borderRadius: 32,
              padding: 'clamp(2.75rem, 6vw, 5.5rem) clamp(1.5rem, 5vw, 4rem)',
              textAlign: 'center',
              backdropFilter: 'blur(16px)',
            }}
          >
            <div aria-hidden style={{ position: 'absolute', top: 0, left: '12%', right: '12%', height: 1, background: 'linear-gradient(90deg, transparent, var(--accent-teal), transparent)', opacity: 0.55, pointerEvents: 'none' }} />
            <div aria-hidden style={{ position: 'absolute', top: -90, left: '50%', transform: 'translateX(-50%)', width: 360, height: 360, borderRadius: '50%', background: '#00e5d1', opacity: 0.16, filter: 'blur(100px)', pointerEvents: 'none' }} />
            <div aria-hidden style={{ position: 'absolute', bottom: -120, right: -40, width: 320, height: 320, borderRadius: '50%', background: 'var(--accent-purple)', opacity: 0.08, filter: 'blur(110px)', pointerEvents: 'none' }} />

            <div style={{ position: 'relative', zIndex: 1, maxWidth: 640, margin: '0 auto' }}>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ delay: 0.08, duration: 0.6 }}
                style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(2rem, 4.5vw, 3.2rem)', fontWeight: 300, color: 'var(--text-primary)', margin: '0 0 1.6rem', lineHeight: 1.12, letterSpacing: '-0.03em' }}
              >
                {t('blog_page.cta_title')}
              </motion.h2>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ delay: 0.18, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                style={{ display: 'flex', gap: '.9rem', justifyContent: 'center', flexWrap: 'wrap' }}
              >
                <a href={waLink(t('blog_page.wa_message'))} target="_blank" rel="noopener noreferrer" className="trial-btn" style={{ padding: '1rem 1.8rem', borderRadius: 980, fontWeight: 700, fontFamily: 'var(--font-heading)', fontSize: '0.95rem', textDecoration: 'none' }}>
                  {t('blog_page.cta_primary')}
                </a>
                <a href="/servicios" className="ghost-btn" style={{ padding: '1rem 1.8rem', borderRadius: 980, fontWeight: 600, fontFamily: 'var(--font-heading)', fontSize: '0.95rem', textDecoration: 'none' }} onClick={(e) => { e.preventDefault(); navigate('/servicios'); }}>
                  {t('blog_page.cta_secondary')}
                </a>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      <style>{`
        /* El hero forzaba #070708 fijo sin importar el tema; el cuerpo del
           artículo justo debajo no tiene fondo propio y en oscuro muestra el
           negro puro de la página (#000000) — esos dos negros distintos
           chocaban justo en el borde entre hero y contenido. En oscuro el
           hero ahora es transparente (deja ver el mismo #000 de la página);
           en claro sigue siendo el fondo oscuro fijo, a propósito, para que
           el texto blanco del hero siga siendo legible. */
        .blog-post-hero { background: #070708; }
        [data-theme='dark'] .blog-post-hero { background: transparent; }

        .trial-btn {
          background: #fff;
          color: #000;
          border: none;
          transition: background 0.2s ease;
        }
        .trial-btn:hover { background: #e8e8ed; }
        [data-theme='light'] .trial-btn { background: #000; color: #fff; }
        [data-theme='light'] .trial-btn:hover { background: #1d1d1f; }

        .ghost-btn {
          display: inline-flex; align-items: center; justify-content: center;
          background: rgba(255,255,255,0.04); color: var(--text-primary);
          border: 1px solid var(--border-lighter);
          transition: background 0.2s ease, border-color 0.2s ease;
        }
        .ghost-btn:hover { background: rgba(255,255,255,0.07); border-color: var(--accent-teal); }
        [data-theme='light'] .ghost-btn { background: rgba(0,0,0,0.03); }
        [data-theme='light'] .ghost-btn:hover { background: rgba(0,0,0,0.06); }

        .prod-cta {
          /* Mismo ajuste que en BlogPage.tsx: el borde cerraba en
             rgba(8,8,8,.6) (semi-transparente) en vez de negro puro,
             dejando un contorno visible contra el negro real de la página. */
          background: radial-gradient(120% 140% at 50% 0%, rgba(0,229,209,0.15) 0%, rgba(139,92,246,0.11) 48%, #000 100%);
        }
        [data-theme='light'] .prod-cta {
          background: radial-gradient(120% 140% at 50% 0%, rgba(0,229,209,0.16) 0%, rgba(139,92,246,0.1) 48%, rgba(255,255,255,0.9) 100%);
          box-shadow: 0 20px 50px -30px rgba(0,0,0,0.25);
        }
      `}</style>
    </motion.div>
  );
};

export default BlogPostPage;
